import complaintService from "@/views/complaint/complaintService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";

import type { Complaint, ComplaintNew, ComplaintQuery, CanDeleteComplaint } from "./complaint";
import type { AxiosResponse } from "axios";

const URL = "complaints";
const ENTITY_NAME = "Complaint";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Complaint>(apiHosts.AQMS, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)

 */
const getComplaints = (params: ComplaintQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Int} id
 */
const getComplaintById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewComplaint = async (ids: ComplaintIds) => {
  const defaultApp = {
    data: {
      ...complaintService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<ComplaintNew, any>;

  return defaultApp;
};

/**
 *
 * @param {Object} ids Object having either an `id` for an Application or a `facilitySiteId`, `permitId`, and type
 * @param {ApiOptions} config
 * @returns {api.get}
 */

type ComplaintIds = {
  id?: string | number;
};
const getComplaintByIdOrNew = (ids: ComplaintIds, config = {}) => {
  if (!ids.id) {
    return getNewComplaint(ids);
  } else {
    return getComplaintById(ids.id, config) as Promise<AxiosResponse<ComplaintNew, any>>;
  }
};

const createComplaint = (complaint: Complaint, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, complaint, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateComplaint = (complaint: Complaint, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + complaint.id, complaint, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Complaint} complaint This cannot be a 'ref' to a complaint
 */
const saveComplaint = (complaint: Complaint, config = {}) => {
  if (complaint.id) {
    return updateComplaint(complaint, config);
  } else {
    return createComplaint(complaint, config);
  }
};

/**
 * @param {Int} id
 */
const deleteComplaintById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteApi = apiService<CanDeleteComplaint, Paging<CanDeleteComplaint>>(apiHosts.AQMS, axiosParams);
const canDeleteComplaint = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteComplaintById,
  getComplaints,
  getComplaintById,
  getNewComplaint,
  getComplaintByIdOrNew,
  createComplaint,
  updateComplaint,
  saveComplaint,
  canDeleteComplaint,
};
